/**
 * Simprosys Google Shopping Feed's Automated Discounts script (gsf_spd_*) computes
 * its discount off the raw Shopify variant price and injects a `.gsf-spd-price`
 * element without awareness of this store's unit display convention. Box products
 * need that amount converted to /m2; every other sales unit already uses the
 * correct amount and only needs its original unit label preserved.
 *
 * This takes over the on-page presentation using the same two numbers Simprosys
 * already computed (no re-implementation of pv2/JWT verification), applying the
 * tile-area conversion only when Shopify says the product is sold by /box.
 *
 * Simprosys re-runs its own logic more than once per page (an instant client-side
 * JWT decode, then an AJAX-confirmed pass) and its own selectors re-scan
 * `.price_money:not(.price__secondary)` / `.price_old` on every pass — reusing
 * either class for our own corrected price lets Simprosys rediscover and
 * re-hide it (it ships `!important` CSS hiding `.gsf-spd-price-block` /
 * `.gsf-spd-sale-price-block`, which a plain inline style can't override
 * anyway). So our replacement lives entirely under its own class names that
 * none of Simprosys's selectors ever target.
 */
function initGsfSpdM2PriceFix() {
  var priceCtn = document.querySelector('.single-product-price-ctn');
  if (!priceCtn) return;

  var uomInput = (priceCtn.getAttribute('data-uom-input') || '').trim();
  var usesSquareMeterPricing = uomInput === '/box';
  if (!uomInput) return;

  var tileArea = parseFloat(priceCtn.getAttribute('data-tile-area'));
  if (usesSquareMeterPricing && !(tileArea > 0)) return;

  var primaryPriceEl = priceCtn.querySelector('.price_money:not(.price__secondary)');
  var boxRefEl = priceCtn.querySelector('.price_money.price__secondary.box-price');
  var boxUnitEl = priceCtn.querySelector('.price_unit.box-price-unit');
  var unitEl = priceCtn.querySelector('.price_unit:not(.box-price-unit)');
  var offerNoteEl = priceCtn.querySelector('[data-gsf-spd-offer-note]');
  if (!primaryPriceEl) return;

  var originalBoxRefText = boxRefEl ? boxRefEl.textContent : null;
  var unitOriginalParent = unitEl ? unitEl.parentNode : null;
  var unitOriginalNextSibling = unitEl ? unitEl.nextSibling : null;

  function parseMoney(text) {
    return text ? parseFloat(text.replace(/[^0-9.-]/g, '')) : NaN;
  }

  function formatMoney(amount) {
    return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  var ourEl = null;
  var lastSignature = null;

  function forceHidden(el) {
    if (!el) return;
    if (el.style.getPropertyValue('display') !== 'none' || el.style.getPropertyPriority('display') !== 'important') {
      el.style.setProperty('display', 'none', 'important');
    }
  }

  function forceVisible(el) {
    if (!el) return;
    if (el.style.getPropertyValue('display') !== 'inline' || el.style.getPropertyPriority('display') !== 'important') {
      el.style.setProperty('display', 'inline', 'important');
    }
  }

  function removeOurOverlay() {
    if (ourEl) {
      if (unitEl && unitOriginalParent) unitOriginalParent.insertBefore(unitEl, unitOriginalNextSibling);
      ourEl.remove();
      ourEl = null;
    }
    lastSignature = null;
    primaryPriceEl.style.removeProperty('display');
    if (boxRefEl && originalBoxRefText !== null) {
      boxRefEl.textContent = originalBoxRefText;
      boxRefEl.style.removeProperty('display');
    }
    if (boxUnitEl) boxUnitEl.style.removeProperty('display');
    if (offerNoteEl) offerNoteEl.hidden = true;
  }

  function applyM2Correction() {
    var gsfPrice = priceCtn.querySelector('.gsf-spd-price') || document.querySelector('.product-info .gsf-spd-price');
    var regularEl = gsfPrice && gsfPrice.querySelector('.gsf-spd-regular-price');
    var saleEl = gsfPrice && gsfPrice.querySelector('.gsf-spd-sale-price');

    if (!regularEl || !saleEl) {
      // Simprosys cleared its own popup (discount window ended) — revert to the
      // plain price if we'd taken over the display.
      if (ourEl) removeOurOverlay();
      return;
    }

    var regularUnitPrice = parseMoney(regularEl.textContent);
    var saleUnitPrice = parseMoney(saleEl.textContent);
    if (!(regularUnitPrice > 0) || !(saleUnitPrice > 0)) return;

    // Keep the unit-agnostic Simprosys popup hidden and render the explicit
    // Shopify sales unit through the theme's own price presentation.
    forceHidden(gsfPrice);

    var signature = [regularUnitPrice, saleUnitPrice, uomInput, tileArea].join('|');
    if (signature !== lastSignature || !ourEl) {
      lastSignature = signature;

      var regularDisplayPrice = usesSquareMeterPricing ? regularUnitPrice / tileArea : regularUnitPrice;
      var saleDisplayPrice = usesSquareMeterPricing ? saleUnitPrice / tileArea : saleUnitPrice;

      if (!ourEl) {
        ourEl = document.createElement('span');
        ourEl.className = 'gsf-spd-m2-price';
        ourEl.innerHTML = '<span class="gsf-spd-m2-was"></span><span class="gsf-spd-m2-now"></span>';
        // primaryPriceEl is wrapped inside Simprosys's own .gsf-spd-price-block by
        // this point (it wraps it before creating .gsf-spd-price), and that
        // wrapper is hidden via `!important` CSS — inserting next to the price
        // element itself would land inside that hidden wrapper too.
        var insertionAnchor = primaryPriceEl.closest('.gsf-spd-price-block') || primaryPriceEl;
        insertionAnchor.insertAdjacentElement('afterend', ourEl);
      }

      ourEl.querySelector('.gsf-spd-m2-was').textContent = formatMoney(regularDisplayPrice);
      var nowEl = ourEl.querySelector('.gsf-spd-m2-now');
      nowEl.textContent = formatMoney(saleDisplayPrice);
      // Move (not clone) the original unit label in — leaving both visible
      // would show "/m2" twice.
      if (unitEl && unitEl.parentNode !== nowEl) nowEl.appendChild(unitEl);
    }

    forceHidden(primaryPriceEl);
    if (offerNoteEl) offerNoteEl.hidden = false;

    // Simprosys's own gsfShowNewPriceOnProductDetail() explicitly hides this
    // box-price reference (`.toggle(!display_spd_price)`) whenever it's
    // showing its own discount popup, on the assumption that showing the box
    // price twice would be redundant. Since we hide its popup and show our
    // own /m2 conversion instead, this reference is the only remaining place
    // the (now discounted) box price is visible — the site normally always
    // shows both a box and an /m2 price side by side, so put it back.
    if (usesSquareMeterPricing && boxRefEl) {
      var boxRefText = ' = ' + formatMoney(saleUnitPrice);
      if (boxRefEl.textContent !== boxRefText) boxRefEl.textContent = boxRefText;
      forceVisible(boxRefEl);
    }
    if (usesSquareMeterPricing) forceVisible(boxUnitEl);
  }

  applyM2Correction();
  new MutationObserver(applyM2Correction).observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });

  var visibilityObserver = new MutationObserver(function () {
    if (ourEl) applyM2Correction();
  });
  if (boxRefEl) visibilityObserver.observe(boxRefEl, { attributes: true, attributeFilter: ['class', 'style'] });
  if (boxUnitEl) visibilityObserver.observe(boxUnitEl, { attributes: true, attributeFilter: ['class', 'style'] });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGsfSpdM2PriceFix);
} else {
  initGsfSpdM2PriceFix();
}
